import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import {
  ConsumerReviewsModel,
  ConsumerReviewsEntities,
  ConsumerReviewsPaths,
} from 'client/data/models/consumer-reviews';
import { ConsumerReviewsParams } from 'site-modules/shared/utils/consumer-reviews/params';
import { getConsumerReviewsUrl, getWriteReviewUrl } from 'site-modules/shared/utils/core-link-constructor';
import { appendTrademarkCharacter } from 'site-modules/shared/utils/inventory-utils/append-trademark-character';
import {
  REVIEW_LINK,
  ReviewSummaryLinks,
} from 'site-modules/shared/components/review-summary-links/review-summary-links';
import { RatingDistribution } from 'site-modules/consumer-reviews/components/consumer-review-summary/rating-distribution/rating-distribution';
import { Link } from 'site-modules/shared/components/link/link';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';

import './consumer-review-summary.scss';

export function ConsumerReviewSummaryUI({
  params,
  vehicle = null,
  addLoading = noop,
  ratingsCount = null,
  showTotalReviewText = true,
  showHeader = true,
  ratingStarLinkout = true,
  displayVehicle = null,
  className = null,
  headerClasses = 'heading-3',
  vehicleFilter = null,
  reviewsFilter = null,
  clickableRatingDistribution = false,
  hideSummaryLinks = false,
  prevYearRating = null,
  hasUsedReviews = false,
}) {
  const {
    make: { slug: makeSlug, name: makeName },
    model: { slug: modelSlug, name: modelName },
    submodels: { slug: submodelSlug },
    year,
  } = vehicle;
  const hasNewReviews = ratingsCount && ratingsCount.totalReviews > 0;

  const urlParams = {
    makeSlug,
    modelSlug,
    year,
    styleId: params.styleId,
  };

  const labelVehicle = appendTrademarkCharacter({ make: makeName, str: `${year} ${makeName} ${modelName}` });
  const vehicleDisplayName = displayVehicle || `${labelVehicle}`;

  let subHeading = `Read what other owners think about the ${vehicleDisplayName}.`;
  if (!hasNewReviews) {
    subHeading = `There are no consumer reviews for the ${vehicleDisplayName}.`;
  }

  const header = (
    <header className="consumer-review-summary-header">
      <h2 className={headerClasses}>Consumer reviews</h2>
      {hasUsedReviews && !!prevYearRating && (
        <Fragment>
          <h3 className="heading-4 mt-1_5 mb-0_5">Owner Reviews</h3>
          <div className="mb-1">
            <span className="heading-3 me-0_5">
              {prevYearRating.toFixed(1)}
              <span className="visually-hidden">&nbsp;out of 5 stars</span>
            </span>
            <RatingStars
              className="d-inline-block heading-3 text-primary-darker"
              rating={prevYearRating}
              hideForScreenReader
            />
          </div>
        </Fragment>
      )}
      <h3 className="size-16 fw-normal text-gray-darker mb-1_25">
        {subHeading}{' '}
        {!hasNewReviews && hasUsedReviews && (
          <Fragment>
            The rating displayed is for previous years of the {makeName} {modelName}.
          </Fragment>
        )}
      </h3>
    </header>
  );

  if (!hasNewReviews) {
    return header;
  }

  const averageRating = (
    <Fragment>
      <span className="average-user-rating d-inline-block heading-5 me-0_25 me-md-0_5">
        {ratingsCount.ratingAggregation.averageStars.toFixed(1)} <span className="visually-hidden">out of 5 stars</span>
      </span>
      <RatingStars
        className="rating-stars d-inline-block heading-3 text-primary-darker"
        rating={ratingsCount.ratingAggregation.averageStars}
        hideForScreenReader /* notice we are using hideForScreenReader because we have added 'out of 5 stars' to the span above */
      />
      {showTotalReviewText && <div className="review-count text-gray-darker">{ratingsCount.totalReviews} reviews</div>}
    </Fragment>
  );
  const averageRatingClassName = classnames('average-rating order-first', {
    'pt-0_25': hideSummaryLinks,
  });
  const averageRatingFragment = ratingStarLinkout ? (
    <Link
      to={getConsumerReviewsUrl(urlParams)}
      className={classnames('average-rating-link d-block', averageRatingClassName)}
    >
      {averageRating}
    </Link>
  ) : (
    <div className={averageRatingClassName}>{averageRating}</div>
  );

  return (
    <Fragment>
      {showHeader && header}
      <div className={classnames('consumer-review-summary d-flex flex-column d-md-block clearfix', className)}>
        <div className="summary-ratings mt-0_75 mt-md-0 float-md-end">
          {ratingsCount.percents.map((percent, index) => {
            const key = `${index}-${percent}`;
            return (
              <RatingDistribution
                key={key}
                percentageRated={percent}
                rating={ratingsCount.percents.length - index}
                vehicleFilter={vehicleFilter}
                reviewFilter={reviewsFilter}
                clickable={clickableRatingDistribution}
                addLoading={addLoading}
              />
            );
          })}
        </div>
        {hideSummaryLinks ? (
          <div className="order-first d-flex align-items-start">
            {averageRatingFragment}{' '}
            <Button
              tag={Link}
              to={getWriteReviewUrl({ makeSlug, modelSlug, submodelSlug, year, styleId: params.styleId })}
              color="outline-primary-b"
              size="lg"
              className="ms-1_5 px-0_75 py-0_5 rounded-8"
              data-tracking-id="crr_write_review"
              data-tracking-value={`Write a ${year} ${makeName} ${modelName} review`}
            >
              Write a review{' '}
              <span className="visually-hidden">
                of {year} {makeName} {modelName}
              </span>
            </Button>
          </div>
        ) : (
          <Fragment>
            {averageRatingFragment}
            <ReviewSummaryLinks
              className="mb-xl-2"
              vehicle={vehicle}
              styleId={params.styleId}
              reviewsCount={ratingsCount.totalReviews}
              linksToRender={[REVIEW_LINK.WRITE_REVIEW, REVIEW_LINK.ALL_FOR_SALE, REVIEW_LINK.PHOTOFLIPPER]}
              writeReviewTrackingId="crr_write_review"
              allForSaleTrackingId="crr_all_for_sale"
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
}
ConsumerReviewSummaryUI.propTypes = {
  params: ConsumerReviewsParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  addLoading: PropTypes.func,
  ratingsCount: ConsumerReviewsEntities.ConsumerReviewsRatingsCount,
  showTotalReviewText: PropTypes.bool,
  showHeader: PropTypes.bool,
  ratingStarLinkout: PropTypes.bool,
  displayVehicle: PropTypes.string,
  className: PropTypes.string,
  headerClasses: PropTypes.string,
  reviewsFilter: ConsumerReviewsEntities.ReviewsFilter,
  vehicleFilter: ConsumerReviewsEntities.VehicleFilter,
  clickableRatingDistribution: PropTypes.bool.isRequired,
  hideSummaryLinks: PropTypes.bool,
  // TODO: Review after core-5497
  prevYearRating: PropTypes.number,
  hasUsedReviews: PropTypes.bool,
};

export const ConsumerReviewSummary = connectToModel(ConsumerReviewSummaryUI, {
  ratingsCount: bindToPath(({ vehicle = null, params, isMMYBased = true }) => {
    if (params.styleId) {
      return ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPathByStyleId(params.styleId);
    }
    return params.submodel && !isMMYBased
      ? ConsumerReviewsPaths.buildSubmodelConsumerReviewsRatingsCountPathFromVehicle(vehicle)
      : ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPathFromVehicle(vehicle);
  }, ConsumerReviewsModel),
});

ConsumerReviewSummary.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  params: ConsumerReviewsParams.isRequired,
  isMMYBased: PropTypes.bool,
  reviewsFilter: ConsumerReviewsEntities.ReviewsFilter,
  vehicleFilter: ConsumerReviewsEntities.VehicleFilter,
  clickableRatingDistribution: PropTypes.bool,
  hideSummaryLinks: PropTypes.bool,
};
